/**
 * CD-8740 — `oso-auth` is a **user-profile cache, not a credential store**.
 *
 * Authentication is carried entirely by the HttpOnly `access_token` /
 * `access_refresh` cookies minted by oso-api. Nothing in this module can
 * authenticate a request, and nothing here is authoritative about whether the
 * user has a session: that answer comes from the boot session probe
 * (`PUT /sessions`, see `configureApp.js`).
 *
 * What it still does is hold the signed-in user's profile (`{ user }`) so that
 * the many synchronous consumers of `AuthData.get()?.user` — the route guard,
 * `withCurrentUser`, `useAdminGuard`, GTM and Honeybadger context — keep
 * working without every one of them becoming async.
 *
 * `store()` defensively strips `token` / `refreshToken` before persisting, so a
 * stale or unexpected API response can never put a credential back into
 * localStorage.
 */

/**
 * Bumped 2 → 3 at the cookie-auth cutover (CD-8740). `get()` returns
 * `undefined` unless the stored version matches exactly, so every browser
 * carrying a pre-cutover entry — which held a bearer token and a refresh token —
 * is treated as signed out on its first load after deploy.
 *
 * Note this only works because it is a value the SPA voluntarily checks. It has
 * no effect on a cookie; it is effective at cutover because no browser holds a
 * session cookie yet.
 */
export const CURRENT_TOKEN_VERSION = 3;

const storeKey = "oso-auth";
const errorMessage =
  "No access to localStorage. Please update or change security settings of your browser";

const readStorage = () => {
  if (window === undefined || window.localStorage === undefined) throw errorMessage;
  return window.localStorage;
};

const AuthData = {
  /**
   * Cache the signed-in user's profile.
   * @param {object} authData an auth response body, e.g. `{ user, redirectTo }`.
   */
  store: authData => {
    if (authData === undefined) console.error("There is no auth data provided");

    const storage = readStorage();

    // Never persist a credential, whatever we were handed.
    const { token, refreshToken, ...profile } = authData || {};

    storage.setItem(
      storeKey,
      JSON.stringify({ ...profile, tokenVersion: CURRENT_TOKEN_VERSION })
    );
  },
  get: () => {
    const authData = readStorage().getItem(storeKey);
    if (!authData || authData === "undefined") return;

    let parsed;
    try {
      parsed = JSON.parse(authData);
    } catch (_e) {
      return;
    }

    if (parsed && parsed.tokenVersion === CURRENT_TOKEN_VERSION) return parsed;
  },
  /**
   * Forget the cached profile. Does **not** end the server-side session — that
   * is `DELETE /sessions` (see `sagas/loginFlow.ts`).
   */
  drop: () => {
    readStorage().removeItem(storeKey);
  }
};

/**
 * One-time cutover cleanup, run once at boot before any session decision.
 *
 * Removes an `oso-auth` entry that was written by a previous token version, is
 * unparseable, or still carries a `token` / `refreshToken`.
 *
 * It deliberately does **not** touch the referral journey's own `'user'` entry.
 * That entry holds no credential (the credential is the `referral` cookie, which
 * survives this deploy), it expires itself via `tokenExpiry`, and there is no
 * referral session probe to recover from wiping it — so removing it would sign
 * out anyone mid-referral for no security benefit.
 */
export const purgeLegacyAuthData = () => {
  try {
    const storage = readStorage();
    const raw = storage.getItem(storeKey);
    if (!raw) return;

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (_e) {
      storage.removeItem(storeKey);
      return;
    }

    const carriesCredential = Boolean(parsed && (parsed.token || parsed.refreshToken));
    const wrongVersion = !parsed || parsed.tokenVersion !== CURRENT_TOKEN_VERSION;

    if (carriesCredential || wrongVersion) storage.removeItem(storeKey);
  } catch (_e) {
    // localStorage unavailable (private mode, blocked cookies). Nothing to clean.
  }
};

export default AuthData;
